/**
 * ConfirmationScreen — shown after the AI emits <complete /> (Quiet Trust theme).
 *
 * Displays all collected values grouped by section.
 * Patient can edit any value inline before confirming.
 * "Confirm & Submit" -> calls onConfirm. "Go back" -> returns to chat.
 */

import React, { useState } from 'react';

interface FieldDef {
  key: string;
  label: string;
  type: string;
  required: boolean;
}

interface Props {
  collectedFields: Record<string, unknown>;
  fieldDefs: FieldDef[];
  onConfirm: (confirmedData: Record<string, unknown>) => void;
  onBack: () => void;
  isSubmitting: boolean;
}

// Fields that should never be shown in the confirmation view
const HIDDEN_KEYS = new Set(['signatureRequired', 'ssnRequired', '_transcriptOnly']);

// --- Inline SVG icons (no emoji) -------------------------------------------

function ClipboardCheckIcon(): React.ReactElement {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 4H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <path d="m9 13 2 2 4-4" />
    </svg>
  );
}

function PencilIcon(): React.ReactElement {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

function CheckIcon({ color = '#fff' }: { color?: string }): React.ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function ArrowLeftIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

function LockIcon(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ci-text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function SpinnerIcon(): React.ReactElement {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ animation: 'spin 0.8s linear infinite' }}>
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}

export default function ConfirmationScreen({
  collectedFields,
  fieldDefs,
  onConfirm,
  onBack,
  isSubmitting,
}: Props): React.ReactElement {
  const [editedFields, setEditedFields] = useState<Record<string, unknown>>(
    () => ({ ...collectedFields }),
  );
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue,  setEditValue]  = useState('');

  const displayFields = fieldDefs.filter(
    (f) =>
      !HIDDEN_KEYS.has(f.key) &&
      f.type !== 'signature' &&
      f.type !== 'button' &&
      !f.key.toLowerCase().includes('ssn'),
  );

  const sections = groupFields(displayFields);

  const startEdit = (key: string) => {
    setEditingKey(key);
    setEditValue(String(editedFields[key] ?? ''));
  };

  const commitEdit = () => {
    if (editingKey !== null) {
      setEditedFields((prev) => ({ ...prev, [editingKey]: editValue }));
    }
    setEditingKey(null);
    setEditValue('');
  };

  const handleConfirm = () => {
    onConfirm(editedFields);
  };

  return (
    <div className="ci-portal ci-enter" style={styles.container}>
      <div style={styles.header}>
        <span style={styles.headerIcon} aria-hidden="true"><ClipboardCheckIcon /></span>
        <div>
          <h2 style={styles.title}>Please review your information</h2>
          <p style={styles.subtitle}>Make any corrections, then confirm to submit.</p>
        </div>
      </div>

      {sections.map((section) => (
        <div key={section.title} style={styles.section}>
          {section.title && <h3 style={styles.sectionTitle}>{section.title}</h3>}
          {section.fields.map((f) => {
            const val = editedFields[f.key];
            const isEditing = editingKey === f.key;
            const isEmpty = val === undefined || val === null || val === '';

            return (
              <div key={f.key} style={styles.fieldRow}>
                <span style={styles.fieldLabel}>{f.label}</span>
                {isEditing ? (
                  <div style={styles.editGroup}>
                    <input
                      className="ci-focusable"
                      style={styles.editInput}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={commitEdit}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); }}
                      autoFocus
                    />
                    <button style={styles.saveBtn} onClick={commitEdit} aria-label="Save">
                      <CheckIcon />
                    </button>
                  </div>
                ) : (
                  <div style={styles.valueGroup}>
                    <span style={{ ...styles.fieldValue, color: isEmpty ? 'var(--ci-text-muted)' : 'var(--ci-text)' }}>
                      {isEmpty ? '—' : formatValue(val)}
                    </span>
                    <button style={styles.editBtn} onClick={() => startEdit(f.key)} aria-label={`Edit ${f.label}`} title="Edit">
                      <PencilIcon />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <div style={styles.actions}>
        <button style={styles.backBtn} onClick={onBack} disabled={isSubmitting}>
          <ArrowLeftIcon />
          Go back
        </button>
        <button
          style={{ ...styles.confirmBtn, opacity: isSubmitting ? 0.7 : 1 }}
          onClick={handleConfirm}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <><SpinnerIcon />Submitting...</>
          ) : (
            <><CheckIcon />Confirm &amp; Submit</>
          )}
        </button>
      </div>

      <p style={styles.privacyNote}>
        <LockIcon />
        Your information is encrypted and transmitted securely.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouping heuristic
// ---------------------------------------------------------------------------

interface Section {
  title: string;
  fields: FieldDef[];
}

const SECTION_PREFIXES: Array<[RegExp, string]> = [
  [/^(firstName|lastName|name|dob|birthDate|gender|sex|ssn|phone|email|address|zip|city|state)/i, 'Personal Information'],
  [/^(insurance|policyHolder|groupNumber|memberId|employer)/i, 'Insurance'],
  [/^(emergency|emergencyContact)/i, 'Emergency Contact'],
  [/^(medical|condition|allergy|medication|surgery|hospitalization)/i, 'Medical History'],
  [/^(chiefComplaint|reasonForVisit|symptoms|pain|duration)/i, 'Reason for Visit'],
  [/^(hipaa|consent|signature|authorization)/i, 'Consent'],
];

function getSectionTitle(key: string): string {
  for (const [regex, title] of SECTION_PREFIXES) {
    if (regex.test(key)) return title;
  }
  return 'Other';
}

function groupFields(fields: FieldDef[]): Section[] {
  const map = new Map<string, FieldDef[]>();
  for (const f of fields) {
    const title = getSectionTitle(f.key);
    if (!map.has(title)) map.set(title, []);
    map.get(title)!.push(f);
  }
  return Array.from(map.entries()).map(([title, flds]) => ({ title, fields: flds }));
}

// ---------------------------------------------------------------------------
// Value formatter
// ---------------------------------------------------------------------------

function formatValue(val: unknown): string {
  if (val === true)  return 'Yes';
  if (val === false) return 'No';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) return val.join(', ');
  return String(val);
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  container: {
    background:   '#fff',
    borderRadius: 'var(--ci-radius-lg)',
    border:       '1px solid var(--ci-border-light)',
    padding:      '20px',
    maxWidth:     560,
    margin:       '0 auto',
    fontFamily:   'var(--ci-font)',
  },
  header: {
    display:       'flex',
    alignItems:    'flex-start',
    gap:           12,
    marginBottom:  20,
    paddingBottom: 16,
    borderBottom:  '1px solid var(--ci-border-light)',
  },
  headerIcon: {
    flexShrink:     0,
    width:          36,
    height:         36,
    borderRadius:   'var(--ci-radius)',
    background:     'var(--ci-primary-light)',
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  title:    { margin: '0 0 4px', fontSize: 17, fontWeight: 700, color: 'var(--ci-text)' },
  subtitle: { margin: 0, fontSize: 13, color: 'var(--ci-text-muted)' },

  section:      { marginBottom: 16 },
  sectionTitle: { margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: 'var(--ci-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' },

  fieldRow:   { display: 'flex', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--ci-border-light)', gap: 8 },
  fieldLabel: { flex: 1, fontSize: 13, color: 'var(--ci-text-muted)', minWidth: 120 },
  valueGroup: { display: 'flex', alignItems: 'center', gap: 6 },
  fieldValue: { fontSize: 13, fontWeight: 500 },
  editBtn:    { display: 'inline-flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ci-text-muted)', padding: '0 2px' },

  editGroup: { display: 'flex', alignItems: 'center', gap: 4 },
  editInput: { padding: '6px 8px', border: '1px solid var(--ci-primary)', borderRadius: 'var(--ci-radius)', fontSize: 13, color: 'var(--ci-text)', outline: 'none', fontFamily: 'var(--ci-font)' },
  saveBtn:   { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'var(--ci-success)', color: '#fff', border: 'none', borderRadius: 'var(--ci-radius)', padding: '4px 7px', cursor: 'pointer', minHeight: 30 },

  actions: {
    display:        'flex',
    justifyContent: 'space-between',
    marginTop:      20,
    paddingTop:     16,
    borderTop:      '1px solid var(--ci-border-light)',
    gap:            8,
  },
  backBtn: {
    display:       'inline-flex',
    alignItems:    'center',
    gap:           6,
    padding:       '11px 16px',
    minHeight:     44,
    background:    'none',
    border:        '1px solid var(--ci-border)',
    borderRadius:  'var(--ci-radius)',
    fontSize:      14,
    cursor:        'pointer',
    color:         'var(--ci-text-body)',
    fontFamily:    'var(--ci-font)',
  },
  confirmBtn: {
    flex:           1,
    display:        'inline-flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            6,
    padding:        '11px 20px',
    minHeight:      44,
    background:     'var(--ci-primary)',
    color:          '#fff',
    border:         'none',
    borderRadius:   'var(--ci-radius)',
    fontSize:       14,
    fontWeight:     600,
    cursor:         'pointer',
    fontFamily:     'var(--ci-font)',
  },
  privacyNote: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            5,
    marginTop:      12,
    fontSize:       11,
    color:          'var(--ci-text-muted)',
  },
};
